// Student credential login — shared, MIRRORED logic.
//
// This file exists as two byte-identical copies:
//   src/shared/auth/student-login.ts             (browser / Vite — login page)
//   supabase/functions/_shared/student-login.ts  (Deno — provisioning)
//
// A credential student signs in with password auth backed by a synthetic,
// never-delivered email derived from the institute slug + a login handle. The
// provisioning side (Edge Function) and the login side (browser) MUST derive
// that email byte-identically or the student silently cannot sign in — so the
// two copies are kept in sync and a unit test asserts they are identical.

/** Domain for synthetic, never-delivered student-login emails. */
export const STUDENT_EMAIL_DOMAIN = 'students.internal';

export const HANDLE_MIN_LENGTH = 3;
export const HANDLE_MAX_LENGTH = 40;

/**
 * Normalize a login handle (username) to its canonical form: trimmed,
 * lowercased, keeping only [a-z0-9._-]. The same raw input always yields the
 * same handle on both the provisioning and login sides.
 */
export function normalizeHandle(raw: string): string {
  return raw
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9._-]+/g, '');
}

/** Normalize an institute slug the same way it is stored (lowercase kebab). */
export function normalizeSlug(raw: string): string {
  return raw
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, '');
}

/** True when a normalized handle is within the allowed length bounds. */
export function isValidHandle(handle: string): boolean {
  return handle.length >= HANDLE_MIN_LENGTH && handle.length <= HANDLE_MAX_LENGTH;
}

/**
 * Build the synthetic email that backs a credential student's auth account.
 * Deterministic in (slug, handle) so the login page needs no server lookup.
 */
export function syntheticEmail(slug: string, handle: string): string {
  return `${normalizeHandle(handle)}@${normalizeSlug(slug)}.${STUDENT_EMAIL_DOMAIN}`;
}

/** Origin-relative path of an institute's student-login page. */
export function studentLoginPath(slug: string): string {
  return `/i/${normalizeSlug(slug)}/login`;
}
